import { MAP_VIEWS, CURRENT_LOCATION_ZOOM } from "./consts.js";
import { queryElements } from "./dom.js";
import { initMap } from "./map.js";
import { createMarkerLayer } from "./markers.js";
import { createCardPanel } from "./card.js";
import { fetchPropertiesInBounds } from "./api.js";
import { getPropertyType, getSearchConditions } from "./state.js";
import { setupKeywordSearch } from "./keyword.js";

/** @typedef {import("./markers.js").MarkerLayer} MarkerLayer */

/**
 * 地図検索ページのエントリポイント
 *
 * DOM 取得・初期化・「イベント → 処理」の配線だけを担う。
 * モジュール同士は互いを知らず、つながりはすべてここに書く
 *
 * @returns {void}
 */
const main = () => {
    const els = queryElements();
    if (!els.root || !els.propertyContainer) return;

    const apiKey = document
        .querySelector('meta[name="google-api-key"]')
        ?.getAttribute("content");
    const mapId = document
        .querySelector('meta[name="google-map-id"]')
        ?.getAttribute("content");
    if (!apiKey || !mapId) return;

    // ローディング表示の操作
    const showLoading = () => els.loading?.removeAttribute("data-hidden");
    const hideLoading = () => els.loading?.setAttribute("data-hidden", "");

    /**
     * 地図の初期化と各モジュールの配線
     * @returns {Promise<void>}
     */
    const init = async () => {
        // サイトモードに応じた初期表示位置（都道府県版: data-area の県 / 総合版: 東北全体を引きで）
        const view = MAP_VIEWS[els.root.dataset.area] ?? MAP_VIEWS.default;

        const { map, AdvancedMarkerElement } = await initMap({
            root: els.root,
            apiKey,
            mapId,
            view,
        });

        /** @type {MarkerLayer|null} */
        let markers = null;

        const card = createCardPanel({
            map,
            els,
            AdvancedMarkerElement,
            onClose: () => markers?.deselectAll(),
        });

        markers = createMarkerLayer({
            map,
            AdvancedMarkerElement,
            onPinClick: (marker, property) => {
                markers.select(marker);
                card.open(property);
            },
        });

        // bounds 取得 → fetch → マーカー更新の統一フロー
        // AbortController で常に最新のリクエストだけが反映されるようにする
        /** @type {AbortController|null} */
        let controller = null;
        /** @type {() => Promise<void>} */
        const refresh = async () => {
            const bounds = map.getBounds();
            if (!bounds) return;

            // 前のリクエストを中断して自分が最新になる
            controller?.abort();
            controller = new AbortController();
            const { signal } = controller;

            showLoading();
            try {
                const northEast = bounds.getNorthEast();
                const southWest = bounds.getSouthWest();
                const items = await fetchPropertiesInBounds({
                    type: getPropertyType(),
                    latFrom: southWest.lat(),
                    latTo: northEast.lat(),
                    lngFrom: southWest.lng(),
                    lngTo: northEast.lng(),
                    conditions: getSearchConditions(),
                    signal,
                });
                // await 中に新しい refresh が走った場合、古い結果で上書きしない
                if (signal.aborted) return;
                markers.update(items);
            } catch (error) {
                // 中断は正常系。ログを出さない
                if (error.name === "AbortError") return;
                console.error("物件データの取得に失敗しました:", error);
            } finally {
                // 中断された古いリクエストが最新のローディングを消さないようにする
                if (!signal.aborted) hideLoading();
            }
        };

        // 現在地マーカー（青丸）。初回作成後は位置だけ更新して使い回す
        /** @type {google.maps.marker.AdvancedMarkerElement|null} */
        let currentLocationMarker = null;
        /** @type {(position: { lat: number, lng: number }) => void} */
        const showCurrentLocationMarker = (position) => {
            if (!currentLocationMarker) {
                const dot = document.createElement("div");
                dot.className = "pg-search-map__current-location";
                currentLocationMarker = new AdvancedMarkerElement({
                    map,
                    position,
                    content: dot,
                });
            } else {
                currentLocationMarker.position = position;
            }
        };

        /**
         * 現在地を取得して地図を現在地へ移動する
         * @returns {void}
         */
        const moveToCurrentLocation = () => {
            if (!navigator.geolocation) {
                alert("お使いのブラウザでは現在地を取得できません");
                return;
            }
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    const position = {
                        lat: coords.latitude,
                        lng: coords.longitude,
                    };
                    showCurrentLocationMarker(position);
                    map.panTo(position);
                    map.setZoom(
                        Math.max(map.getZoom(), CURRENT_LOCATION_ZOOM),
                    );
                },
                (error) => {
                    alert(
                        error.code === error.PERMISSION_DENIED
                            ? "位置情報の利用が許可されていません。ブラウザの設定をご確認ください"
                            : "現在地を取得できませんでした",
                    );
                },
                { timeout: 10000 },
            );
        };

        // 地図操作ボタン（現在地 / ズーム）。デフォルトコントロールの代わりに独自ボタンで操作する
        const toolActions = {
            current: moveToCurrentLocation,
            "zoom-in": () => map.setZoom(map.getZoom() + 1),
            "zoom-out": () => map.setZoom(map.getZoom() - 1),
        };
        els.toolButtons.forEach((button) => {
            button.addEventListener("click", () => {
                toolActions[button.dataset.type]?.();
            });
        });

        // キーワード検索（駅/住所/施設）で該当地点へ地図を移動する
        // 移動後は idle が発火するため、マーカーの再取得は refresh に任せる
        setupKeywordSearch({ map, els });

        // マップの表示範囲が変更されたときにマーカーを再取得
        map.addListener("idle", refresh);
    };

    init().catch((error) => {
        console.error("地図の初期化に失敗しました:", error);
    });
};

export default main;
